<template>
  <div class="request-invoice">
    <LoadingOverlayComponent v-if="isLoading" />
    <h4 class="font-serif mb-4">
      {{ $t("requestInvoice.submit_invoice") }}
    </h4>
    <div class="row text-center mb-3 align-middle g-0">
      <div class="col-12 col-md-3 align-self-center">
        <PendingPayoutFigure
          variant="summary"
          :eurAmount="pendingTotal"
          :payoutCurrency="company.payout_currency"
          :indicativePayoutRate="company.indicative_payout_rate"
        />
        <p class="mb-0 fs-9 fw-bold">
          {{ $t("requestInvoice.money_pending") }}
        </p>
      </div>
      <div
        id="sum-explainer"
        class="card col-12 col-md-9 row bg-secondary-subtle p-2 g-0 mt-2 mt-md-0"
      >
        <div class="col-4">
          <p class="mb-0">{{ formatMoney(sales) }} €</p>
          <p class="mb-0 fs-9">{{ $t("requestInvoice.sales_revenue") }}</p>
        </div>
        <div class="col-4">
          <p class="mb-0">{{ formatMoney(vat) }} €</p>
          <p class="mb-0 fs-9">
            {{ $t("requestInvoice.sales_vat") }}
          </p>
        </div>
        <div class="col-4">
          <p class="mb-0">{{ formatMoney(fees) }} €</p>
          <p class="mb-0 fs-9">{{ $t("requestInvoice.service_fees_vat") }}</p>
        </div>
      </div>
    </div>
    <div class="card mt-sm-2 mb-4">
      <div class="card-body p-1 px-3 p-sm-2 px-sm-3 bg-body-highlight">
        <div
          class="d-flex align-items-center"
          @click="toggleCollapse"
          role="button"
        >
          <div class="avatar avatar-x1 me-2">
            <font-awesome-icon
              icon="rectangle-list"
              class="text-secondary-emphasis"
            />
          </div>
          <div class="flex-1 fw-bold fs-9">
            {{ $t("requestInvoice.detailed_overview") }}
          </div>
          <div class="btn btn-text p-2">
            {{ $t("requestInvoice.view_details") }}
          </div>
        </div>
        <div class="text-body-secondary mt-1" v-show="showCollapse">
          <hr />
          <h5 class="fs-8 mb-3">{{ $t("requestInvoice.invoice_items") }}</h5>
          <div class="fs-9 mb-4 card p-2 p-lg-0 overflow-hidden">
            <div
              class="row bg-secondary-subtle mb-2 fw-bold d-none d-lg-flex p-1 g-0"
            >
              <div class="col-4 p-1">
                <p class="mb-0">{{ $t("requestInvoice.item") }}</p>
              </div>
              <div class="col-2 p-1 text-end">
                <p class="mb-0">{{ $t("requestInvoice.quantity") }}</p>
              </div>
              <div class="col-2 p-1 text-end">
                <p class="mb-0">{{ $t("requestInvoice.unit_price") }}</p>
              </div>
              <div class="col-2 p-1 text-end">
                <p class="mb-0">{{ $t("requestInvoice.total_excl_vat") }}</p>
              </div>
              <div class="col-2 p-1 text-end">
                <p class="mb-0">{{ $t("requestInvoice.vat") }}</p>
              </div>
            </div>
            <InvoiceItemDisplayComponent
              v-for="item in pendingInvoiceItems.filter(
                (item) => item.unit_price < 0
              )"
              :key="item.id"
              :invoiceItem="item"
            />
          </div>
          <div
            v-if="
              pendingInvoiceItems.filter((item) => item.unit_price > 0).length >
              0
            "
            class="mb-3"
          >
            <h5 class="fs-8 mb-3">
              {{ $t("requestInvoice.offset_service_fees") }}
            </h5>
            <div class="fs-9 mb-3 card p-2 p-lg-0 overflow-hidden">
              <div
                class="row bg-secondary-subtle mb-2 fw-bold d-none d-lg-flex p-1 g-0"
              >
                <div class="col-4 p-1">
                  <p class="mb-0">{{ $t("requestInvoice.item") }}</p>
                </div>
                <div class="col-2 p-1 text-end">
                  <p class="mb-0">{{ $t("requestInvoice.quantity") }}</p>
                </div>
                <div class="col-2 p-1 text-end">
                  <p class="mb-0">{{ $t("requestInvoice.unit_price") }}</p>
                </div>
                <div class="col-2 p-1 text-end">
                  <p class="mb-0">{{ $t("requestInvoice.total_excl_vat") }}</p>
                </div>
                <div class="col-2 p-1 text-end">
                  <p class="mb-0">{{ $t("requestInvoice.vat") }}</p>
                </div>
              </div>
              <InvoiceItemDisplayComponent
                v-for="item in pendingInvoiceItems.filter(
                  (item) => item.unit_price > 0
                )"
                :key="item.id"
                :invoiceItem="item"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <hr />

    <h4 class="fs-8 font-serif mb-3">
      {{ $t("requestInvoice.invoice_details") }}
    </h4>

    <form @submit.prevent="requestInvoice" novalidate>
      <div class="row">
        <div class="col-12 col-sm-6">
          <div class="mb-3">
            <label for="invoiceNumber" class="form-label fs-9 p-0 text-body">{{
              $t("requestInvoice.invoice_number")
            }}</label>
            <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': v$.localInvoiceNumber.$error }"
              id="invoiceNumber"
              v-model="localInvoiceNumber"
              @blur="v$.localInvoiceNumber.$touch"
              :placeholder="$t('requestInvoice.invoice_number_placeholder')"
              maxlength="35"
            />
            <div
              class="invalid-feedback"
              v-for="error of v$.localInvoiceNumber.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
          <div class="mb-3">
            <label for="reference" class="form-label fs-9 p-0 text-body">{{
              $t("requestInvoice.reference")
            }}</label>
            <input
              type="text"
              class="form-control"
              id="reference"
              v-model="localReference"
              :placeholder="$t('requestInvoice.reference_placeholder')"
              maxlength="35"
            />
          </div>
          <!-- No need to show VAT code field to Estonian private individuals -->
          <div
            class="mb-3"
            v-if="company.country != 'EE' || company.registry_code?.length == 8"
          >
            <label for="vat" class="form-label fs-9 p-0 text-body">{{
              $t("requestInvoice.vat_number")
            }}</label>
            <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': v$.localVat.$error }"
              id="vat"
              v-model="localVat"
              :placeholder="$t('requestInvoice.vat_number_placeholder')"
              @blur="v$.localVat.$touch"
            />
            <div
              class="invalid-feedback"
              v-for="error of v$.localVat.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
          <div class="mb-3">
            <label for="bankAccount" class="form-label fs-9 p-0 text-body">
              {{ $t("requestInvoice.bank_account_iban") }}
            </label>
            <input
              type="text"
              class="form-control"
              :class="{ 'is-invalid': v$.localBankAccount.$error }"
              id="bankAccount"
              v-model="localBankAccount"
              :placeholder="$t('requestInvoice.bank_account_placeholder')"
              @blur="v$.localBankAccount.$touch"
            />
            <div
              class="invalid-feedback"
              v-for="error of v$.localBankAccount.$errors"
              :key="error.$uid"
            >
              {{ error.$message }}
            </div>
          </div>
        </div>

        <div class="col-12 col-sm-6 mb-3">
          <label for="paymentHeader" class="form-label fs-9 p-0 text-body">{{
            $t("requestInvoice.contact_block_on_invoice")
          }}</label>
          <textarea
            type="text"
            class="form-control"
            :class="{ 'is-invalid': v$.localPaymentHeader.$error }"
            id="paymentHeader"
            v-model="localPaymentHeader"
            :maxlength="PAYMENT_HEADER_MAX_LENGTH"
            :placeholder="$t('requestInvoice.contact_block_placeholder')"
            @blur="v$.localPaymentHeader.$touch"
          ></textarea>
          <div
            class="invalid-feedback"
            v-for="error of v$.localPaymentHeader.$errors"
            :key="error.$uid"
          >
            {{ error.$message }}
          </div>
        </div>
      </div>
      <div class="form-check mb-3">
        <input
          type="checkbox"
          class="form-check-input"
          id="monthlyRollup"
          v-model="localMonthlyRollup"
        />
        <label class="form-check-label fs-9" for="monthlyRollup">
          {{ $t("requestInvoice.monthly_rollup_label") }}
        </label>
      </div>
      <button
        type="submit"
        class="btn btn-primary w-100 mb-3"
        :disabled="!canSubmit"
      >
        {{ $t("requestInvoice.submit_invoice") }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import InvoiceItemDisplayComponent from "@/components/common/InvoiceItemDisplayComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import { formatMoney } from "@/composables/formatUtils";
import {
  calculateTotalAmountFromInvoiceItems,
  calculateTotalAmountFromInvoiceItemsWithVAT,
  feesFilter,
  fromUnits,
  hasPositivePendingPayout,
  salesFilter,
} from "@/composables/money/moneyUtils";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { useValidators } from "@/composables/useValidators";
import { PAYMENT_HEADER_MAX_LENGTH } from "@/money/invoice/invoiceLimits";
import PendingPayoutFigure from "@/money/PendingPayoutFigure.vue";
import type { Company, InvoiceItem } from "@/types";
import useVuelidate from "@vuelidate/core";
import { filter, sumBy } from "lodash-es";
import { computed, inject, ref } from "vue";
import { useI18n } from "vue-i18n";

const props = withDefaults(
  defineProps<{
    company: Company;
    pendingInvoiceItems: InvoiceItem[];
    defaultInvoiceNumber?: string;
  }>(),
  { defaultInvoiceNumber: "" }
);

const emit = defineEmits<{
  "invoice-requested": [];
}>();

const { t } = useI18n();
const globalErrorHandler = inject("globalErrorHandler") as (error: any) => void;
const localVat = ref(props.company.vat);
const localBankAccount = ref(props.company.bank_account);
const localPaymentHeader = ref(props.company.payment_header);
const localInvoiceNumber = ref(props.defaultInvoiceNumber);
const localReference = ref("");
const localMonthlyRollup = ref(
  Boolean(props.company.monthly_notification_rollup)
);
const showCollapse = ref(false);
const isLoading = ref(false);

const sales = computed(() => {
  return calculateTotalAmountFromInvoiceItems(
    props.pendingInvoiceItems,
    salesFilter
  );
});

const fees = computed(() => {
  return calculateTotalAmountFromInvoiceItemsWithVAT(
    props.pendingInvoiceItems,
    feesFilter
  );
});

const pendingTotal = computed(() => sales.value + fees.value + vat.value);

const canSubmit = computed(() =>
  hasPositivePendingPayout(props.pendingInvoiceItems)
);

const vat = computed(
  () =>
    -fromUnits(
      sumBy(filter(props.pendingInvoiceItems, salesFilter), "vat_units")
    )
);

const toggleCollapse = () => {
  showCollapse.value = !showCollapse.value;
};

// INPUT VALIDATION SECTION
const {
  required,
  maxLength,
  estonianVATNumber,
  latvianVATNumber,
  lithuanianVATNumber,
  iban,
} = useValidators(t);

const vat_check = computed(() => {
  if (props.company.country === "EE") {
    return estonianVATNumber;
  } else if (props.company.country === "LV") {
    return latvianVATNumber;
  } else if (props.company.country === "LT") {
    return lithuanianVATNumber;
  } else {
    return null;
  }
});

const rules = computed(() => ({
  localInvoiceNumber: { required, maxLength: maxLength(35) },
  localVat: vat_check.value ? { vat_check: vat_check.value } : {},
  localBankAccount: { required, iban },
  localPaymentHeader: {
    required,
    maxLength: maxLength(PAYMENT_HEADER_MAX_LENGTH),
  },
}));

const v$ = useVuelidate(rules, {
  localInvoiceNumber,
  localVat,
  localBankAccount,
  localPaymentHeader,
});

const checkValidity = () => {
  v$.value.$touch();
  return !v$.value.$error;
};

async function requestInvoice() {
  if (!checkValidity()) {
    return;
  }
  isLoading.value = true;
  try {
    const response = await goSolidApi.post(
      `/api/company/${props.company.id}/generate-invoice`,
      {
        invoice_number: localInvoiceNumber.value,
        vat_number: localVat.value,
        bank_account: localBankAccount.value,
        payment_header: localPaymentHeader.value,
        reference: localReference.value || null,
        monthly_notification_rollup: localMonthlyRollup.value,
      }
    );
    if (response.status === 200) {
      emit("invoice-requested");
    } else {
      console.error("Error requesting invoice");
      throw new Error("Error requesting invoice");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
}
</script>

<style scoped lang="scss">
form {
  .form-label {
    text-transform: none;
  }
}
#paymentHeader {
  min-height: 10rem;
}

#sum-explainer {
  flex-direction: row;
}
</style>
